import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer } from "lucide-react";
import LabelTile from "@/components/label/LabelTile";
import { loadLabelFormat, resolveFormat } from "@/lib/labelFormat";

/**
 * Bulk carton labels — honours the format saved on the Custom Labels page.
 *
 * URL: /labels/bulk?box_nos=WPL1,WPL2,WPL3&autoprint=1
 *
 * Cartons are chunked according to the current preset's perPage (2 for the
 * default 104mm × 50mm thermal sheet, 1 for 50×50 / 100×100 / 100×150, 6 for
 * the A4 grid). One window.print() streams the whole batch as one job.
 */
export default function CartonLabelsBulk() {
  const [search] = useSearchParams();
  const raw = (search.get("box_nos") || "").trim();
  const autoPrint = search.get("autoprint") === "1";
  const boxNos = raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : [];

  const [labels, setLabels] = useState(null);
  const [progress, setProgress] = useState({ done: 0, total: boxNos.length });
  const printedRef = React.useRef(false);

  const fmt = useMemo(() => resolveFormat(loadLabelFormat()), []);
  const { preset, family, scale, fieldSet, customText, fieldSizes } = fmt;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (boxNos.length === 0) { setLabels([]); return; }
      const settled = await Promise.allSettled(
        boxNos.map((bn) => api.get(`/cartons/${bn}/activity`).then((r) => r.data)),
      );
      if (cancelled) return;
      const out = settled.map((s, i) => s.status === "fulfilled"
        ? s.value
        : { error: true, box_no: boxNos[i] });
      setLabels(out);
      setProgress({ done: out.length, total: boxNos.length });
    })();
    return () => { cancelled = true; };
  }, [raw]);

  useEffect(() => {
    if (!autoPrint || !labels || printedRef.current) return;
    if (labels.length === 0) return;
    printedRef.current = true;
    const t = setTimeout(() => {
      try { window.focus(); } catch (_e) { /* no-op */ }
      window.print();
    }, 600);
    return () => clearTimeout(t);
  }, [autoPrint, labels]);

  if (labels === null) {
    return (
      <div className="p-8 font-mono text-xs text-zinc-500" data-testid="bulk-labels-loading">
        Loading {progress.total} label{progress.total === 1 ? "" : "s"}…
      </div>
    );
  }

  if (boxNos.length === 0) {
    return <div className="p-8 font-mono text-xs text-red-700">No carton numbers provided. Use ?box_nos=WPL1,WPL2,…</div>;
  }

  const valid = labels.filter((l) => !l.error);
  const missing = labels.filter((l) => l.error);

  // Chunk according to the saved format's perPage.
  const pageGroups = [];
  for (let i = 0; i < valid.length; i += preset.perPage) {
    pageGroups.push(valid.slice(i, i + preset.perPage));
  }

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white p-4 print:p-0" data-testid="bulk-labels-page">
      <div className="max-w-[520px] mx-auto flex items-center justify-between mb-3 text-xs print:hidden">
        <a href="/inventory" className="font-mono text-zinc-500 hover:text-orange-700">← Inventory</a>
        <div className="font-mono text-[11px] text-zinc-600" data-testid="bulk-labels-summary">
          {valid.length} / {boxNos.length} ready · {pageGroups.length} page{pageGroups.length === 1 ? "" : "s"} · {preset.label}
        </div>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white text-[11px] font-mono uppercase tracking-wider"
          data-testid="bulk-print-btn"
        >
          <Printer size={12} /> Print all
        </button>
      </div>

      {missing.length > 0 && (
        <div className="max-w-[520px] mx-auto mb-3 print:hidden">
          {missing.map((m, i) => (
            <div key={`err-${i}`} className="border-2 border-dashed border-red-400 bg-red-50 px-3 py-1.5 text-[11px] font-mono text-red-700 mb-1">
              {m.box_no || boxNos[i]} — not found, skipped
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-col items-center gap-4 print:gap-0">
        {pageGroups.map((group, idx) => (
          <div
            key={idx}
            data-pair-card
            data-testid={`bulk-label-pair-${idx}`}
            className="bg-zinc-100 print:bg-white flex flex-row flex-wrap"
            style={{
              width: `${preset.pageW}mm`,
              height: `${preset.pageH}mm`,
              gap: preset.perPage > 1 ? "4mm" : "0",
              padding: preset.key === "a4grid" ? "5mm" : "0",
            }}
          >
            {group.map((d, li) => (
              <LabelTile key={li} d={d} preset={preset}
                fontFamily={family.css} fontScale={scale.mult}
                fieldSet={fieldSet} customText={customText}
                fieldSizes={fieldSizes} />
            ))}
          </div>
        ))}
      </div>

      <style>{`
        @media print {
          @page {
            size: ${preset.pageW}mm ${preset.pageH}mm;
            margin: 0;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          [data-testid="bulk-labels-page"] { padding: 0 !important; }
          [data-testid="bulk-labels-page"] > div:first-child { display: none !important; }
          [data-pair-card] {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: wrap !important;
            justify-content: flex-start !important;
            width: ${preset.pageW}mm !important;
            height: ${preset.pageH}mm !important;
            gap: ${preset.perPage > 1 ? "4mm" : "0"} !important;
            margin: 0 !important;
            padding: ${preset.key === "a4grid" ? "5mm" : "0"} !important;
            page-break-after: always;
            page-break-inside: avoid;
            break-after: page;
            break-inside: avoid;
            background: white !important;
          }
          [data-pair-card]:last-child {
            page-break-after: auto;
            break-after: auto;
          }
          [data-label-card] {
            box-sizing: border-box !important;
            page-break-inside: avoid;
            break-inside: avoid;
          }
        }
      `}</style>
    </div>
  );
}
